import ruMonthNames from './ruMonthNames';

// Полные названия месяцев — для подписи выбранного периода. Короткие (ruMonthNames) идут на
// сами таблетки и на ось графика, где 12 подряд «Январь» не помещаются.
const ruMonthNamesFull = [
    'Январь',
    'Февраль',
    'Март',
    'Апрель',
    'Май',
    'Июнь',
    'Июль',
    'Август',
    'Сентябрь',
    'Октябрь',
    'Ноябрь',
    'Декабрь',
];

export const QUARTERS = [
    { key: 1, label: 'I кв', months: [1, 2, 3] },
    { key: 2, label: 'II кв', months: [4, 5, 6] },
    { key: 3, label: 'III кв', months: [7, 8, 9] },
    { key: 4, label: 'IV кв', months: [10, 11, 12] },
];

const ROMAN = ['I', 'II', 'III', 'IV'];

// Формат, который пишут таблетки: «2026-01,2026-02». Месяц строго 01–12: «2026-13» из ручной
// правки ссылки не должен проходить фронт-валидацию — иначе ломается подпись периода
// («undefined 2026») и бэкенд отвечает 422 вместо отката к периоду по умолчанию. Всё остальное
// считается старым форматом одного диапазона («01.02.2026 — 28.02.2026») — его продолжает
// понимать и бэкенд, чтобы уже разосланные ссылки на дашборд не превращались в 422.
const MONTH_LIST_PATTERN = /^\d{4}-(0[1-9]|1[0-2])(,\d{4}-(0[1-9]|1[0-2]))*$/;
const LEGACY_SEPARATOR = ' — ';

const pad = (value) => String(value).padStart(2, '0');

function isMonthListPeriod(period) {
    return typeof period === 'string' && MONTH_LIST_PATTERN.test(period.trim());
}

/**
 * Собирает значение ?period= из выбранных таблеток.
 *
 * @param {number} year
 * @param {number[]} months Номера месяцев 1..12, в любом порядке.
 * @returns {string}
 */
export function buildPeriodValue(year, months) {
    return months
        .slice()
        .sort((a, b) => a - b)
        .map((month) => `${year}-${pad(month)}`)
        .join(',');
}

/**
 * Восстанавливает состояние таблеток из значения ?period=.
 *
 * Старый диапазон разбирается, только если он совпадает с целыми месяцами одного года —
 * ровно так выглядят ссылки, сохранённые из прежнего режима «Месяц»/«Год». Произвольный
 * диапазон таблетками не выражается, поэтому для него возвращается null: показать его на
 * таблетках было бы враньём, дашборд в этом случае откатывается к периоду по умолчанию.
 *
 * @param {string} period
 * @returns {{year: number, months: number[]}|null}
 */
export function parsePeriodSelection(period) {
    // Дубликаты месяцев из ручной правки ссылки («2026-01,2026-01») схлопываются — иначе они
    // просочились бы в состояние таблеток и в подпись периода («Янв, Янв 2026»).
    const months = Array.from(new Set(parsePeriodMonths(period)));

    if (months.length === 0) {
        return null;
    }

    const years = new Set(months.map((month) => Number(month.slice(0, 4))));

    if (years.size !== 1) {
        return null;
    }

    if (!isMonthListPeriod(period) && !coversWholeMonths(period)) {
        return null;
    }

    return {
        year: months[0] ? Number(months[0].slice(0, 4)) : null,
        months: months.map((month) => Number(month.slice(5, 7))),
    };
}

/**
 * Месяцы, которые период покрывает, как «YYYY-MM». Для старого диапазона — все месяцы, которых
 * он касается хотя бы одним днём (график тренда подсвечивает именно затронутые месяцы).
 *
 * @param {string} period
 * @returns {string[]}
 */
function parsePeriodMonths(period) {
    if (!period) {
        return [];
    }

    if (isMonthListPeriod(period)) {
        return period.trim().split(',');
    }

    const [from, to] = parseLegacyRange(period);

    if (!from || !to) {
        return [];
    }

    const months = [];
    const cursor = new Date(from.getFullYear(), from.getMonth(), 1);
    const end = new Date(to.getFullYear(), to.getMonth(), 1);

    while (cursor <= end) {
        months.push(`${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}`);
        cursor.setMonth(cursor.getMonth() + 1);
    }

    return months;
}

/**
 * Человеческая подпись периода: «2026 год», «III квартал 2026», «Январь — Март 2026»,
 * «Январь, Июль 2026». Старый диапазон уже читаем как есть.
 *
 * @param {string} period
 * @returns {string}
 */
export function formatPeriodLabel(period) {
    if (!period) {
        return '';
    }

    if (!isMonthListPeriod(period)) {
        return period;
    }

    const byYear = new Map();

    parsePeriodMonths(period).forEach((month) => {
        const year = Number(month.slice(0, 4));

        if (!byYear.has(year)) {
            byYear.set(year, []);
        }

        byYear.get(year).push(Number(month.slice(5, 7)));
    });

    return Array.from(byYear.entries())
        .map(([year, months]) =>
            formatYearLabel(
                year,
                months.sort((a, b) => a - b),
            ),
        )
        .join('; ');
}

function formatYearLabel(year, months) {
    if (months.length === 12) {
        return `${year} год`;
    }

    const quarter = QUARTERS.find(
        (item) => item.months.length === months.length && item.months.every((month) => months.includes(month)),
    );

    if (quarter) {
        return `${ROMAN[quarter.key - 1]} квартал ${year}`;
    }

    const isContiguous = months.every((month, index) => index === 0 || month === months[index - 1] + 1);

    if (months.length === 1) {
        return `${ruMonthNamesFull[months[0] - 1]} ${year}`;
    }

    if (isContiguous) {
        return `${ruMonthNamesFull[months[0] - 1]} — ${ruMonthNamesFull[months[months.length - 1] - 1]} ${year}`;
    }

    return `${months.map((month) => ruMonthNames[month - 1]).join(', ')} ${year}`;
}

function parseLegacyRange(period) {
    const parts = String(period).split(LEGACY_SEPARATOR);
    const from = parseDisplayDate(parts[0]);
    const to = parts[1] ? parseDisplayDate(parts[1]) : from;

    return [from, to];
}

function parseDisplayDate(value) {
    if (!value) {
        return null;
    }

    const [day, month, year] = value.trim().split('.').map(Number);

    if (!day || !month || !year) {
        return null;
    }

    const date = new Date(year, month - 1, day);

    return Number.isNaN(date.getTime()) ? null : date;
}

// Диапазон начинается 1-м числом месяца и заканчивается последним днём месяца — то есть
// выражается целыми месяцами и может быть показан таблетками без потери точности.
function coversWholeMonths(period) {
    const [from, to] = parseLegacyRange(period);

    if (!from || !to) {
        return false;
    }

    const lastDayOfMonth = new Date(to.getFullYear(), to.getMonth() + 1, 0).getDate();

    return from.getDate() === 1 && to.getDate() === lastDayOfMonth;
}
